import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, User, Plus, X, Gift, Clock, Check, Mail, Save, Link2, Unlink, RefreshCw, CheckCircle2, ChevronDown, ChevronRight, Eye, Settings2, ScanLine, FileText, Pencil, TrendingUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { setFreebieSkus } from "@/lib/freebieCalc";
import UserPermissionsModal from "@/components/UserPermissionsModal";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { MultiSelect } from "@/components/ui/multi-select";
import WarehouseMaster from "@/components/WarehouseMaster";

export default function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Permission modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalUser, setModalUser] = useState(null);
  const [modalMode, setModalMode] = useState("edit"); // 'approve' | 'edit'

  // Freebie SKUs management
  const [freebieSkuList, setFreebieSkuList] = useState([]);
  const [newSku, setNewSku] = useState("");
  const [savingFreebies, setSavingFreebies] = useState(false);

  // Manager approval discount threshold (%)
  const [approvalThreshold, setApprovalThreshold] = useState("60");
  const [savingThreshold, setSavingThreshold] = useState(false);

  // Monthly Business Targets
  const [businessTargets, setBusinessTargets] = useState([]);
  const [newTargetMonth, setNewTargetMonth] = useState("");
  const [newTargetValue, setNewTargetValue] = useState("");
  const [savingTarget, setSavingTarget] = useState(false);

  // Email Configuration (default To / CC recipients)
  const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  const [emailTo, setEmailTo] = useState([]);
  const [emailCc, setEmailCc] = useState([]);
  const [newToInput, setNewToInput] = useState("");
  const [newCcInput, setNewCcInput] = useState("");
  const [savingEmailCfg, setSavingEmailCfg] = useState(false);

  // Approval Configuration (Step 1 — config only; strictly Admin-role)
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);
  const isAdmin = currentUser?.role === "admin";
  const [sellersList, setSellersList] = useState([]);
  const [apManagerId, setApManagerId] = useState("");
  const [apManagerManagerId, setApManagerManagerId] = useState("");
  const [apSellerIds, setApSellerIds] = useState([]);
  const [savingApprovalCfg, setSavingApprovalCfg] = useState(false);

  // Gmail (Email Automation) connection
  const [gmailStatus, setGmailStatus] = useState({ connected: false, email: null, last_sync_at: null, connected_at: null });
  const [gmailLoading, setGmailLoading] = useState(false);
  const [gmailConnecting, setGmailConnecting] = useState(false);
  const [gmailMigrating, setGmailMigrating] = useState(false);
  const [migration, setMigration] = useState(null); // { status, total, processed, linked, not_found, skipped, errors, eta_seconds, mode }
  const migrationPollRef = useRef(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [details, setDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const openDetails = async () => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    try {
      const res = await axios.get(`${API}/gmail/backfill-threads/details`, { withCredentials: true });
      setDetails(res.data?.results || []);
    } catch {
      toast.error("Failed to load migration details");
    } finally {
      setDetailsLoading(false);
    }
  };

  // Phase 2 — Automatic Invoice / E-Way Bill detection
  const [autoStatus, setAutoStatus] = useState(null); // { enabled, last_run_at, last_run_stats, detected_total, detected_invoices, detected_eways }
  const [autoScanning, setAutoScanning] = useState(false);
  const [autoToggling, setAutoToggling] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);

  const fetchAutoStatus = async () => {
    try {
      const res = await axios.get(`${API}/gmail/automation/status`, { withCredentials: true });
      setAutoStatus(res.data || null);
    } catch {
      // silent (admin-only)
    }
  };

  const toggleAutomation = async (enabled) => {
    setAutoToggling(true);
    try {
      await axios.put(`${API}/gmail/automation/config`, { enabled }, { withCredentials: true });
      toast.success(enabled ? "Automatic detection enabled" : "Automatic detection disabled");
      fetchAutoStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update setting");
    } finally {
      setAutoToggling(false);
    }
  };

  const runAutoScan = async () => {
    setAutoScanning(true);
    try {
      await axios.post(`${API}/gmail/automation/scan`, {}, { withCredentials: true });
      toast.success("Detection scan started — refreshing shortly…");
      setTimeout(() => { fetchAutoStatus(); setAutoScanning(false); }, 6000);
    } catch (err) {
      setAutoScanning(false);
      toast.error(err?.response?.data?.detail || "Failed to start scan");
    }
  };

  const [autoReprocessing, setAutoReprocessing] = useState(false);
  const runReprocess = async () => {
    setAutoReprocessing(true);
    try {
      await axios.post(`${API}/gmail/automation/process`, {}, { params: { reprocess: true }, withCredentials: true });
      toast.success("Re-extracting & re-validating detected documents…");
      setTimeout(() => { fetchAutoStatus(); setAutoReprocessing(false); }, 6000);
    } catch (err) {
      setAutoReprocessing(false);
      toast.error(err?.response?.data?.detail || "Failed to start reprocessing");
    }
  };

  const openDocs = async () => {
    setDocsOpen(true);
    setDocsLoading(true);
    try {
      const res = await axios.get(`${API}/gmail/automation/detected-documents`, { withCredentials: true });
      setDocs(res.data?.documents || []);
    } catch {
      toast.error("Failed to load detected documents");
    } finally {
      setDocsLoading(false);
    }
  };

  const fmtEta = (secs) => {
    if (secs === null || secs === undefined) return "calculating…";
    if (secs <= 0) return "almost done";
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `~${m}m ${s}s` : `~${s}s`;
  };

  useEffect(() => {
    fetchUsers();
    fetchFreebieSkus();
    fetchApprovalThreshold();
    fetchBusinessTargets();
    fetchEmailConfig();
    fetchGmailStatus();
    if (currentUser?.role === "admin") {
      fetchSellersForApproval();
      fetchApprovalConfig();
    }
    // Reflect OAuth redirect result (?gmail=connected|error)
    const params = new URLSearchParams(window.location.search);
    const g = params.get("gmail");
    if (g === "connected") toast.success("Gmail connected successfully");
    else if (g === "error") {
      const reason = params.get("reason");
      toast.error(reason ? `Gmail connection failed: ${decodeURIComponent(reason)}` : "Gmail connection failed. Please try again.", { duration: 10000 });
    }
    if (g) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const fetchGmailStatus = async () => {
    setGmailLoading(true);
    try {
      const res = await axios.get(`${API}/gmail/status`, { withCredentials: true });
      setGmailStatus(res.data || { connected: false });
      if (res.data?.connected) {
        const m = await fetchMigrationStatus();
        if (m?.status === "running") startMigrationPolling();
        fetchAutoStatus();
      }
    } catch {
      // silent — admin-only endpoint; non-admins won't see this card anyway
    } finally {
      setGmailLoading(false);
    }
  };

  useEffect(() => () => { if (migrationPollRef.current) clearInterval(migrationPollRef.current); }, []);

  const handleConnectGmail = async () => {
    setGmailConnecting(true);
    try {
      const redirectBase = window.location.origin;
      const res = await axios.get(
        `${API}/oauth/gmail/authorize-url`,
        { params: { redirect_base: redirectBase }, withCredentials: true }
      );
      const url = res.data?.authorize_url;
      if (url) window.location.href = url;
      else { toast.error("Could not start Gmail authorization"); setGmailConnecting(false); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start Gmail authorization");
      setGmailConnecting(false);
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm("Disconnect Gmail? Sent quotation emails will no longer be linked to their conversations.")) return;
    try {
      await axios.post(`${API}/gmail/disconnect`, {}, { withCredentials: true });
      toast.success("Gmail disconnected");
      fetchGmailStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to disconnect Gmail");
    }
  };

  const fetchMigrationStatus = async () => {
    try {
      const res = await axios.get(`${API}/gmail/backfill-threads/status`, { withCredentials: true });
      const data = res.data || {};
      setMigration(data.status && data.status !== "idle" ? data : null);
      if (data.status === "running") {
        setGmailMigrating(true);
      } else {
        setGmailMigrating(false);
        if (migrationPollRef.current) { clearInterval(migrationPollRef.current); migrationPollRef.current = null; }
      }
      return data;
    } catch {
      return null;
    }
  };

  const startMigrationPolling = () => {
    if (migrationPollRef.current) clearInterval(migrationPollRef.current);
    migrationPollRef.current = setInterval(fetchMigrationStatus, 3000);
  };

  const handleMigrateThreads = async (mode = "all") => {
    setGmailMigrating(true);
    try {
      await axios.post(`${API}/gmail/backfill-threads`, {}, { params: { mode }, withCredentials: true });
      toast.success(mode === "retry" ? "Retrying unmatched quotations…" : "Migration started in the background");
      await fetchMigrationStatus();
      startMigrationPolling();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.info("A migration is already running");
        await fetchMigrationStatus();
        startMigrationPolling();
      } else {
        setGmailMigrating(false);
        toast.error(err?.response?.data?.detail || "Failed to start migration");
      }
    }
  };

  const fetchEmailConfig = async () => {
    try {
      const res = await axios.get(`${API}/settings/email-config`, { withCredentials: true });
      setEmailTo(res.data?.default_to || []);
      setEmailCc(res.data?.default_cc || []);
    } catch {
      toast.error("Failed to load email configuration");
    }
  };

  const saveEmailConfig = async () => {
    const allTo = [...emailTo, newToInput.trim()].filter(Boolean);
    const allCc = [...emailCc, newCcInput.trim()].filter(Boolean);
    const invalid = [...allTo, ...allCc].find((e) => !EMAIL_RE.test(e));
    if (invalid) { toast.error(`Invalid email address: ${invalid}`); return; }
    setSavingEmailCfg(true);
    try {
      const res = await axios.put(
        `${API}/settings/email-config`,
        { default_to: allTo, default_cc: allCc },
        { withCredentials: true }
      );
      setEmailTo(res.data?.default_to || []);
      setEmailCc(res.data?.default_cc || []);
      setNewToInput("");
      setNewCcInput("");
      toast.success("Email configuration saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save email configuration");
    } finally {
      setSavingEmailCfg(false);
    }
  };


  const fetchFreebieSkus = async () => {
    try {
      const res = await axios.get(`${API}/settings/freebie-skus`, { withCredentials: true });
      setFreebieSkuList(res.data?.skus || []);
    } catch {
      toast.error("Failed to load freebie SKUs");
    }
  };

  const fetchApprovalThreshold = async () => {
    try {
      const res = await axios.get(`${API}/quotations/approval-config`, { withCredentials: true });
      if (res.data?.threshold != null) setApprovalThreshold(String(res.data.threshold));
    } catch {
      /* keep default */
    }
  };

  const saveApprovalThreshold = async () => {
    const val = parseFloat(approvalThreshold);
    if (Number.isNaN(val) || val < 0 || val > 100) {
      toast.error("Enter a threshold between 0 and 100");
      return;
    }
    setSavingThreshold(true);
    try {
      const res = await axios.put(`${API}/quotations/approval-config`, { threshold: val }, { withCredentials: true });
      setApprovalThreshold(String(res.data?.threshold ?? val));
      toast.success("Approval threshold updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save threshold");
    } finally {
      setSavingThreshold(false);
    }
  };

  const fetchSellersForApproval = async () => {
    try {
      const res = await axios.get(`${API}/sellers`, { withCredentials: true });
      setSellersList(res.data || []);
    } catch {
      /* non-admin or error */
    }
  };

  const fetchApprovalConfig = async () => {
    try {
      const res = await axios.get(`${API}/settings/approval-config`, { withCredentials: true });
      setApManagerId(res.data?.manager_user_id || "");
      setApManagerManagerId(res.data?.manager_manager_user_id || "");
      setApSellerIds(res.data?.higher_approval_seller_ids || []);
    } catch {
      /* non-admin or error — leave defaults */
    }
  };

  const saveApprovalConfig = async () => {
    setSavingApprovalCfg(true);
    try {
      const res = await axios.put(
        `${API}/settings/approval-config`,
        {
          manager_user_id: apManagerId || null,
          manager_manager_user_id: apManagerManagerId || null,
          higher_approval_seller_ids: apSellerIds,
        },
        { withCredentials: true }
      );
      setApManagerId(res.data?.manager_user_id || "");
      setApManagerManagerId(res.data?.manager_manager_user_id || "");
      setApSellerIds(res.data?.higher_approval_seller_ids || []);
      toast.success("Approval configuration saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save approval configuration");
    } finally {
      setSavingApprovalCfg(false);
    }
  };

  const fetchBusinessTargets = async () => {
    try {
      const res = await axios.get(`${API}/business-targets`, { withCredentials: true });
      setBusinessTargets(res.data || []);
    } catch {
      /* non-admin or error — leave empty */
    }
  };

  const saveBusinessTarget = async () => {
    const month = (newTargetMonth || "").trim();
    const val = parseFloat(newTargetValue);
    if (!/^\d{4}-\d{2}$/.test(month)) {
      toast.error("Pick a valid month");
      return;
    }
    if (Number.isNaN(val) || val <= 0) {
      toast.error("Business target must be greater than zero");
      return;
    }
    setSavingTarget(true);
    try {
      await axios.put(`${API}/business-targets`, { month, target: val }, { withCredentials: true });
      toast.success("Business target saved");
      setNewTargetMonth("");
      setNewTargetValue("");
      fetchBusinessTargets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save business target");
    } finally {
      setSavingTarget(false);
    }
  };

  const editBusinessTarget = (t) => {
    setNewTargetMonth(t.month);
    setNewTargetValue(String(t.target));
  };

  const deleteBusinessTarget = async (month) => {
    try {
      await axios.delete(`${API}/business-targets/${month}`, { withCredentials: true });
      toast.success("Business target deleted");
      fetchBusinessTargets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete business target");
    }
  };

  const saveFreebieSkus = async (nextList) => {
    setSavingFreebies(true);
    try {
      const res = await axios.put(
        `${API}/settings/freebie-skus`,
        { skus: nextList },
        { withCredentials: true }
      );
      const saved = res.data?.skus || [];
      setFreebieSkuList(saved);
      setFreebieSkus(saved); // sync local cache so KPIs reflect immediately
      toast.success("Freebie SKUs updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save freebie SKUs");
    } finally {
      setSavingFreebies(false);
    }
  };

  const handleAddSku = () => {
    const v = newSku.trim();
    if (!v) return;
    if (freebieSkuList.includes(v)) {
      toast.error("SKU already in list");
      return;
    }
    saveFreebieSkus([...freebieSkuList, v]);
    setNewSku("");
  };

  const handleRemoveSku = (sku) => {
    saveFreebieSkus(freebieSkuList.filter((s) => s !== sku));
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error fetching users");
    } finally {
      setLoading(false);
    }
  };

  const pendingUsers = useMemo(
    () => users.filter((u) => (u.status || "active") === "pending"),
    [users]
  );
  const activeUsers = useMemo(
    () => users.filter((u) => (u.status || "active") === "active"),
    [users]
  );

  const openApprove = (u) => { setModalUser(u); setModalMode("approve"); setModalOpen(true); };
  const openEdit = (u) => { setModalUser(u); setModalMode("edit"); setModalOpen(true); };

  const handleReject = async (u) => {
    const ok = window.confirm(
      `Reject and remove ${u.name || u.email}?\n\nThis deletes the user record. They will need to sign in again to request access.`
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/users/${u.user_id}`, { withCredentials: true });
      toast.success(`${u.name || u.email} rejected`);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reject user");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  const renderUserAvatar = (user) => (
    <div className="flex items-center gap-3">
      {user.picture ? (
        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm font-semibold">
          {user.name?.charAt(0).toUpperCase() || "?"}
        </div>
      )}
      <span className="font-medium">{user.name || "—"}</span>
    </div>
  );

  const renderRoleBadge = (role) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${role === "admin" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
      {role === "admin" ? <Shield className="h-3 w-3" /> : <User className="h-3 w-3" />}
      {role === "admin" ? "Admin" : "User"}
    </span>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="control-panel-title">Control Panel</h1>
        <p className="text-muted-foreground">Manage user approvals and module permissions. Users are added automatically when they sign in with Google and start in "Pending" until you approve them.</p>
      </div>

      {/* Users — split into Pending + Active tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Users ({users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={pendingUsers.length > 0 ? "pending" : "active"} className="w-full">
            <TabsList className="mb-3">
              <TabsTrigger value="pending" data-testid="users-tab-pending">
                <Clock className="h-3.5 w-3.5 mr-1.5" />
                Pending
                {pendingUsers.length > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                    {pendingUsers.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="active" data-testid="users-tab-active">
                <Check className="h-3.5 w-3.5 mr-1.5" />
                Active ({activeUsers.length})
              </TabsTrigger>
            </TabsList>

            {/* PENDING USERS */}
            <TabsContent value="pending">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground" data-testid="no-pending-users">
                          No pending users. All approved! 🎉
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingUsers.map((u) => (
                        <TableRow key={u.user_id} data-testid={`pending-user-row-${u.email}`}>
                          <TableCell>{renderUserAvatar(u)}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {u.created_at ? new Date(u.created_at).toLocaleString() : "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                className="bg-[#32B4AC] hover:bg-[#2A9A93] text-white"
                                onClick={() => openApprove(u)}
                                data-testid={`approve-user-${u.email}`}
                              >
                                <Check className="h-3.5 w-3.5 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                                onClick={() => handleReject(u)}
                                data-testid={`reject-user-${u.email}`}
                              >
                                <X className="h-3.5 w-3.5 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* ACTIVE USERS */}
            <TabsContent value="active">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Modules</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No active users yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      activeUsers.map((u) => (
                        <TableRow key={u.user_id} data-testid={`active-user-row-${u.email}`}>
                          <TableCell>{renderUserAvatar(u)}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>{renderRoleBadge(u.role)}</TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {u.role === "admin"
                              ? <span className="text-amber-700 font-medium">All (admin)</span>
                              : (u.permissions?.length ? `${u.permissions.length} module${u.permissions.length === 1 ? "" : "s"}` : <span className="text-red-500">None</span>)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEdit(u)}
                              data-testid={`edit-user-${u.email}`}
                            >
                              <Shield className="h-3.5 w-3.5 mr-1" />
                              Permissions
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <UserPermissionsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        user={modalUser}
        mode={modalMode}
        onSaved={fetchUsers}
      />

      {/* Approval Configuration (Admin-role only) — Step 1: config only, no routing */}
      {isAdmin && (
        <Card data-testid="approval-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-accent" />
              Approval Configuration
            </CardTitle>
            <p className="text-sm text-muted-foreground pt-1">
              Configure the seller-based approval hierarchy. This is stored for a future routing step and does
              <strong> not</strong> change the current approval workflow yet. Admin-only.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-sm">Manager</Label>
                <SearchableSelect
                  options={users.filter((u) => u.status === "active").map((u) => ({
                    value: u.user_id,
                    label: `${u.name || u.email} (${u.email})`,
                    searchTerms: [u.name, u.email].filter(Boolean),
                  }))}
                  value={apManagerId}
                  onValueChange={setApManagerId}
                  placeholder="Select Manager"
                  searchPlaceholder="Search users..."
                  data-testid="approval-manager-select"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Manager&apos;s Manager</Label>
                <SearchableSelect
                  options={users.filter((u) => u.status === "active").map((u) => ({
                    value: u.user_id,
                    label: `${u.name || u.email} (${u.email})`,
                    searchTerms: [u.name, u.email].filter(Boolean),
                  }))}
                  value={apManagerManagerId}
                  onValueChange={setApManagerManagerId}
                  placeholder="Select Manager's Manager"
                  searchPlaceholder="Search users..."
                  data-testid="approval-manager-manager-select"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Sellers Requiring Manager&apos;s Manager Approval</Label>
              <p className="text-xs text-muted-foreground">Multi-select — search by seller name or code and add as many as needed. Selected sellers appear as chips below.</p>
              <div className="max-w-md">
                <MultiSelect
                  options={sellersList.map((s) => ({
                    value: s.id,
                    label: `${s.seller_name}${s.seller_code ? ` (${s.seller_code})` : ""}`,
                    searchTerms: [s.seller_name, s.seller_code, s.city].filter(Boolean),
                  }))}
                  values={apSellerIds}
                  onValuesChange={setApSellerIds}
                  placeholder="Search & add sellers..."
                  searchPlaceholder="Search by name or code..."
                  emptyMessage="No sellers found."
                  data-testid="approval-seller-multiselect"
                />
              </div>
              <div className="flex flex-wrap gap-2 pt-1" data-testid="approval-seller-chips">
                {apSellerIds.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No sellers added. Sellers not listed here follow the standard approval flow.</p>
                ) : (
                  apSellerIds.map((sid) => {
                    const s = sellersList.find((x) => x.id === sid);
                    const label = s ? `${s.seller_name}${s.seller_code ? ` (${s.seller_code})` : ""}` : sid;
                    return (
                      <span key={sid} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/10 text-accent border border-accent/30 text-xs" data-testid={`approval-seller-chip-${sid}`}>
                        {label}
                        <button type="button" onClick={() => setApSellerIds((prev) => prev.filter((x) => x !== sid))} aria-label={`Remove ${label}`} data-testid={`approval-seller-remove-${sid}`}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={saveApprovalConfig} disabled={savingApprovalCfg} className="bg-[#32B4AC] hover:bg-[#2A9A93] text-white" data-testid="approval-config-save">
                <Save className="h-4 w-4 mr-1.5" />
                {savingApprovalCfg ? "Saving…" : "Save Configuration"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warehouse Master (Admin-only) */}
      {isAdmin && <WarehouseMaster />}

      {/* Freebie SKUs Whitelist */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-accent" />
            Freebie SKUs Whitelist
          </CardTitle>
          <p className="text-sm text-muted-foreground pt-1">
            Exact sku_id match list used by Avg. Freebie % and Total Freebie Value across the app.
            Admin-only. Changes apply instantly.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Input
              value={newSku}
              onChange={(e) => setNewSku(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddSku(); } }}
              placeholder="Enter sku_id (e.g. WCCE-SC-P6-UF)"
              className="max-w-sm"
              disabled={savingFreebies}
              data-testid="freebie-sku-input"
            />
            <Button
              onClick={handleAddSku}
              disabled={savingFreebies || !newSku.trim()}
              className="bg-[#32B4AC] hover:bg-[#1D9E75] text-white"
              data-testid="freebie-sku-add-btn"
            >
              <Plus className="h-4 w-4 mr-1" />Add
            </Button>
          </div>
          {freebieSkuList.length === 0 ? (
            <p className="text-sm text-muted-foreground">No freebie SKUs configured.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="freebie-sku-list">
              {freebieSkuList.map((sku) => (
                <span
                  key={sku}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                  style={{ background: "#E1F5EE", color: "#085041", borderColor: "#32B4AC40" }}
                  data-testid={`freebie-sku-${sku}`}
                >
                  {sku}
                  <button
                    onClick={() => handleRemoveSku(sku)}
                    disabled={savingFreebies}
                    className="ml-0.5 hover:bg-[#32B4AC]/20 rounded-full p-0.5 transition-colors disabled:opacity-40"
                    aria-label={`Remove ${sku}`}
                    data-testid={`freebie-sku-remove-${sku}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground pt-1">
            Total: <span className="font-medium text-[#0F0F0F]">{freebieSkuList.length}</span> SKU{freebieSkuList.length !== 1 ? "s" : ""}
          </p>
        </CardContent>
      </Card>

      {/* Manager Discount Approval Threshold */}
      <Card data-testid="approval-threshold-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-accent" />
            Manager Approval Threshold
          </CardTitle>
          <p className="text-sm text-muted-foreground pt-1">
            Quotations whose <strong>overall</strong> or any <strong>brand-wise</strong> effective discount exceeds this %
            require manager approval before they can be finalized. Admin-only. Applies instantly.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={approvalThreshold}
              onChange={(e) => setApprovalThreshold(e.target.value)}
              className="max-w-[140px]"
              disabled={savingThreshold}
              data-testid="approval-threshold-input"
            />
            <span className="text-sm text-muted-foreground">%</span>
            <Button
              onClick={saveApprovalThreshold}
              disabled={savingThreshold}
              className="bg-[#32B4AC] hover:bg-[#1D9E75] text-white ml-2"
              data-testid="approval-threshold-save-btn"
            >
              <Save className="h-4 w-4 mr-1" />Save
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Business Targets */}
      <Card data-testid="business-targets-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-accent" />
            Monthly Business Targets
          </CardTitle>
          <p className="text-sm text-muted-foreground pt-1">
            Month-specific revenue target used as the denominator for the <strong>Seller Business Contribution</strong> calculation
            while creating/editing quotations. One target per month. Admin-only.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="text-[11px] text-muted-foreground block mb-1">Month</label>
              <Input
                type="month"
                value={newTargetMonth}
                onChange={(e) => setNewTargetMonth(e.target.value)}
                className="w-[170px]"
                disabled={savingTarget}
                data-testid="business-target-month-input"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground block mb-1">Business Target (₹)</label>
              <Input
                type="number"
                min="1"
                step="1"
                value={newTargetValue}
                onChange={(e) => setNewTargetValue(e.target.value)}
                placeholder="e.g. 120000000"
                className="w-[200px]"
                disabled={savingTarget}
                data-testid="business-target-value-input"
              />
            </div>
            <Button
              onClick={saveBusinessTarget}
              disabled={savingTarget || !newTargetMonth || !newTargetValue}
              className="bg-[#32B4AC] hover:bg-[#1D9E75] text-white"
              data-testid="business-target-save-btn"
            >
              <Save className="h-4 w-4 mr-1" />Save
            </Button>
          </div>

          {businessTargets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No business targets configured yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Business Target (₹)</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead>Updated By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {businessTargets.map((t) => (
                    <TableRow key={t.month} data-testid={`business-target-row-${t.month}`}>
                      <TableCell className="font-medium">{t.month}</TableCell>
                      <TableCell className="font-mono">₹{Number(t.target).toLocaleString("en-IN")}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {t.updated_at ? new Date(t.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
                      </TableCell>
                      <TableCell className="text-sm">{t.updated_by_name || "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => editBusinessTarget(t)} title="Edit" data-testid={`business-target-edit-${t.month}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => deleteBusinessTarget(t.month)} title="Delete" data-testid={`business-target-delete-${t.month}`}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Configuration */}
      <Card data-testid="email-config-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-accent" />
            Email Configuration
          </CardTitle>
          <p className="text-sm text-muted-foreground pt-1">
            Default <strong>To</strong> and <strong>CC</strong> recipients used by &quot;Send Quotation via Email&quot;.
            Add, edit, or remove addresses below — no code changes needed. Admin-only.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Default To */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Default To Email IDs</Label>
            <div className="space-y-2" data-testid="email-to-list">
              {emailTo.map((email, idx) => (
                <div key={`to-${idx}`} className="flex items-center gap-2">
                  <Input
                    value={email}
                    onChange={(e) => setEmailTo(emailTo.map((v, i) => (i === idx ? e.target.value : v)))}
                    className="max-w-sm"
                    disabled={savingEmailCfg}
                    data-testid={`email-to-input-${idx}`}
                  />
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => setEmailTo(emailTo.filter((_, i) => i !== idx))}
                    disabled={savingEmailCfg}
                    aria-label="Remove To email"
                    data-testid={`email-to-remove-${idx}`}
                  >
                    <X className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
              {emailTo.length === 0 && (
                <p className="text-sm text-muted-foreground">No default To recipients.</p>
              )}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Input
                value={newToInput}
                onChange={(e) => setNewToInput(e.target.value)}
                placeholder="Add To email ID"
                className="max-w-sm"
                disabled={savingEmailCfg}
                data-testid="email-to-add-input"
              />
            </div>
          </div>

          {/* Default CC */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Default CC Email IDs</Label>
            <div className="space-y-2" data-testid="email-cc-list">
              {emailCc.map((email, idx) => (
                <div key={`cc-${idx}`} className="flex items-center gap-2">
                  <Input
                    value={email}
                    onChange={(e) => setEmailCc(emailCc.map((v, i) => (i === idx ? e.target.value : v)))}
                    className="max-w-sm"
                    disabled={savingEmailCfg}
                    data-testid={`email-cc-input-${idx}`}
                  />
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => setEmailCc(emailCc.filter((_, i) => i !== idx))}
                    disabled={savingEmailCfg}
                    aria-label="Remove CC email"
                    data-testid={`email-cc-remove-${idx}`}
                  >
                    <X className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
              {emailCc.length === 0 && (
                <p className="text-sm text-muted-foreground">No default CC recipients.</p>
              )}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Input
                value={newCcInput}
                onChange={(e) => setNewCcInput(e.target.value)}
                placeholder="Add CC email ID"
                className="max-w-sm"
                disabled={savingEmailCfg}
                data-testid="email-cc-add-input"
              />
            </div>
          </div>

          <Button
            onClick={saveEmailConfig}
            disabled={savingEmailCfg}
            className="bg-[#32B4AC] hover:bg-[#1D9E75] text-white"
            data-testid="email-config-save-btn"
          >
            <Save className="h-4 w-4 mr-1" />
            {savingEmailCfg ? "Saving..." : "Save Email Configuration"}
          </Button>
        </CardContent>
      </Card>

      {/* Email Automation (Gmail connection) */}
      <Card data-testid="email-automation-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-accent" />
            Email Automation
          </CardTitle>
          <p className="text-sm text-muted-foreground pt-1">
            Connect the Gmail account used to send quotations so each sent email is linked to its
            conversation thread. This is the foundation for upcoming automatic invoice detection. Admin-only.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {gmailLoading ? (
            <p className="text-sm text-muted-foreground">Checking connection…</p>
          ) : gmailStatus.connected ? (
            <div className="space-y-4" data-testid="gmail-connected-panel">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                <div className="text-sm">
                  <div className="font-medium text-emerald-800" data-testid="gmail-connected-email">
                    Connected as {gmailStatus.email || "Gmail account"}
                  </div>
                  <div className="text-xs text-emerald-700/80">
                    {gmailStatus.connected_at && (
                      <>Connected {new Date(gmailStatus.connected_at).toLocaleString()}. </>
                    )}
                    Last sync: {gmailStatus.last_sync_at ? new Date(gmailStatus.last_sync_at).toLocaleString() : "never"}
                  </div>
                </div>
              </div>
              {/* Connection actions */}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handleDisconnectGmail}
                  disabled={gmailMigrating}
                  className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid="gmail-disconnect-btn"
                >
                  <Unlink className="h-4 w-4 mr-1" />
                  Disconnect
                </Button>
              </div>

              {/* Migration progress / summary (always visible on main page) */}
              {migration && migration.status !== "idle" && (
                <div className="rounded-lg border p-4 space-y-3" data-testid="gmail-migration-progress">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#0F0F0F]">
                      {migration.status === "running" && "Historical linking in progress…"}
                      {migration.status === "completed" && "Historical Migration Summary"}
                      {migration.status === "failed" && "Migration failed"}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                      migration.status === "running" ? "bg-cyan-50 text-cyan-700 border-cyan-200" :
                      migration.status === "completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      "bg-red-50 text-red-700 border-red-200"}`} data-testid="gmail-migration-status">
                      {migration.status === "running" ? "Running" : migration.status === "completed" ? "Completed" : "Failed"}
                    </span>
                  </div>

                  {migration.status !== "failed" && (
                    <>
                      {migration.status === "running" && (
                        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-[#32B4AC] h-2 rounded-full transition-all"
                            style={{ width: `${migration.eligible ? Math.round((migration.processed / migration.eligible) * 100) : 0}%` }}
                            data-testid="gmail-migration-bar"
                          />
                        </div>
                      )}

                      {/* Context — the full picture (never reprocesses already-linked) */}
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Overview</div>
                        <div className="grid grid-cols-3 gap-3 text-center">
                          <div className="rounded-md bg-slate-50 border py-2"><div className="text-lg font-semibold text-[#0F0F0F]" data-testid="mig-total-quotations">{migration.total_quotations ?? 0}</div><div className="text-[11px] text-muted-foreground">Total Quotations</div></div>
                          <div className="rounded-md bg-slate-50 border py-2"><div className="text-lg font-semibold text-emerald-700" data-testid="mig-already-linked">{migration.already_linked ?? 0}</div><div className="text-[11px] text-muted-foreground">Already Linked</div></div>
                          <div className="rounded-md bg-slate-50 border py-2"><div className="text-lg font-semibold text-cyan-700" data-testid="mig-eligible">{migration.eligible ?? migration.total ?? 0}</div><div className="text-[11px] text-muted-foreground">Eligible for Migration</div></div>
                        </div>
                      </div>

                      {/* This run — outcome of the eligible set */}
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
                          {migration.status === "running" ? `This run (${migration.processed ?? 0}/${migration.eligible ?? migration.total ?? 0} processed)` : "This run"}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                          <div><div className="text-lg font-semibold text-emerald-600" data-testid="mig-linked">{migration.linked ?? 0}</div><div className="text-[11px] text-muted-foreground">Newly Linked</div></div>
                          <div><div className="text-lg font-semibold text-amber-600" data-testid="mig-notfound">{migration.not_found ?? 0}</div><div className="text-[11px] text-muted-foreground">Not Found</div></div>
                          <div><div className="text-lg font-semibold text-slate-500" data-testid="mig-skipped">{migration.skipped ?? 0}</div><div className="text-[11px] text-muted-foreground">Skipped</div></div>
                          <div><div className="text-lg font-semibold text-red-600" data-testid="mig-errors">{migration.errors ?? 0}</div><div className="text-[11px] text-muted-foreground">Errors</div></div>
                        </div>
                      </div>
                      {migration.status === "running" && (
                        <p className="text-[11px] text-muted-foreground text-center" data-testid="mig-eta">
                          Estimated time remaining: {fmtEta(migration.eta_seconds)}
                        </p>
                      )}
                      {migration.status === "completed" && (
                        <div className="flex justify-end">
                          <Button
                            variant="outline" size="sm"
                            onClick={openDetails}
                            data-testid="gmail-view-details-btn"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Details
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                  {migration.status === "failed" && (
                    <p className="text-sm text-red-600" data-testid="mig-error">{migration.error || "Unknown error"}</p>
                  )}
                </div>
              )}

              {/* Advanced / Maintenance — collapsible historical migration controls */}
              <div className="rounded-lg border" data-testid="gmail-advanced-section">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-[#0F0F0F] hover:bg-slate-50 transition-colors"
                  data-testid="gmail-advanced-toggle"
                >
                  <span className="flex items-center gap-2">
                    <Settings2 className="h-4 w-4 text-muted-foreground" />
                    Advanced / Maintenance
                  </span>
                  {advancedOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                {advancedOpen && (
                  <div className="px-4 pb-4 space-y-3 border-t pt-3">
                    <p className="text-[11px] text-muted-foreground">
                      Historical email linking is a one-time setup. New quotations are linked automatically
                      going forward — you only need these controls to re-run or repair past links.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        onClick={() => handleMigrateThreads("all")}
                        disabled={gmailMigrating}
                        className="bg-[#32B4AC] hover:bg-[#1D9E75] text-white"
                        data-testid="gmail-migrate-btn"
                      >
                        <RefreshCw className={`h-4 w-4 mr-1 ${gmailMigrating ? "animate-spin" : ""}`} />
                        {gmailMigrating ? "Linking past emails…" : "Link Past Emails"}
                      </Button>
                      {migration?.status === "completed" && migration?.not_found > 0 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMigrateThreads("retry")}
                          disabled={gmailMigrating}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid="gmail-retry-notfound-btn"
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Retry Not Found ({migration.not_found})
                        </Button>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Runs in the background — you can leave this page; progress is saved and resumes automatically.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="gmail-disconnected-panel">
              <p className="text-sm text-muted-foreground">No Gmail account connected yet.</p>
              <Button
                onClick={handleConnectGmail}
                disabled={gmailConnecting}
                className="bg-[#32B4AC] hover:bg-[#1D9E75] text-white"
                data-testid="gmail-connect-btn"
              >
                <Link2 className="h-4 w-4 mr-1" />
                {gmailConnecting ? "Redirecting…" : "Connect Gmail"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Automatic Invoice / E-Way Bill Detection (Phase 2) — only when Gmail is connected */}
      {gmailStatus.connected && (
        <Card data-testid="auto-detection-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ScanLine className="h-5 w-5 text-accent" />
              Automatic Invoice &amp; E-Way Bill Detection
            </CardTitle>
            <p className="text-sm text-muted-foreground pt-1">
              Every quotation emailed from Omnix is automatically enrolled for live monitoring — its
              Gmail thread is captured at send time and watched for Invoice / E-Way Bill replies, which
              are extracted and validated against the quotation (marked <strong>Ready for Upload</strong>
              or <strong>Validation Failed</strong>). Documents are prepared only — nothing is uploaded
              (that's the next phase). Admin-only.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <div className="text-sm font-medium text-[#0F0F0F]">Automatic monitoring</div>
                <div className="text-xs text-muted-foreground">
                  Watches new quotations sent from Omnix and processes only their future replies.
                </div>
              </div>
              <Switch
                checked={!!autoStatus?.enabled}
                onCheckedChange={toggleAutomation}
                disabled={autoToggling}
                data-testid="auto-detection-toggle"
              />
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-md bg-slate-50 border py-2"><div className="text-lg font-semibold text-[#0F0F0F]" data-testid="auto-detected-total">{autoStatus?.detected_total ?? 0}</div><div className="text-[11px] text-muted-foreground">Documents Detected</div></div>
              <div className="rounded-md bg-slate-50 border py-2"><div className="text-lg font-semibold text-emerald-600" data-testid="auto-detected-invoices">{autoStatus?.detected_invoices ?? 0}</div><div className="text-[11px] text-muted-foreground">Invoices</div></div>
              <div className="rounded-md bg-slate-50 border py-2"><div className="text-lg font-semibold text-cyan-700" data-testid="auto-detected-eways">{autoStatus?.detected_eways ?? 0}</div><div className="text-[11px] text-muted-foreground">E-Way Bills</div></div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Validation (Phase 3)</div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="rounded-md bg-teal-50 border border-teal-200 py-2"><div className="text-lg font-semibold text-teal-700" data-testid="auto-ready-upload">{autoStatus?.ready_for_upload ?? 0}</div><div className="text-[11px] text-muted-foreground">Ready for Upload</div></div>
                <div className="rounded-md bg-red-50 border border-red-200 py-2"><div className="text-lg font-semibold text-red-600" data-testid="auto-validation-failed">{autoStatus?.validation_failed ?? 0}</div><div className="text-[11px] text-muted-foreground">Validation Failed</div></div>
              </div>
            </div>

            {autoStatus?.last_run_stats && (
              <p className="text-[11px] text-muted-foreground" data-testid="auto-last-run">
                Last scan: {autoStatus.last_run_at ? new Date(autoStatus.last_run_at).toLocaleString() : "—"} ·
                {" "}scanned {autoStatus.last_run_stats.scanned} of {autoStatus.last_run_stats.eligible} eligible ·
                {" "}{autoStatus.last_run_stats.invoices_detected ?? 0} invoices, {autoStatus.last_run_stats.eways_detected ?? 0} e-way bills detected ·
                {" "}{autoStatus.last_run_stats.moved_to_processing} moved to Processing
                {(autoStatus.last_run_stats.network_errors ?? 0) > 0 && <> · <span className="text-amber-600">{autoStatus.last_run_stats.network_errors} network/API errors</span></>}
                {(autoStatus.last_run_stats.permanent_errors ?? 0) > 0 && <> · <span className="text-red-600">{autoStatus.last_run_stats.permanent_errors} permanent errors</span></>}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={runAutoScan}
                disabled={autoScanning}
                variant="outline"
                data-testid="auto-scan-now-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${autoScanning ? "animate-spin" : ""}`} />
                {autoScanning ? "Scanning…" : "Run Full Scan (maintenance)"}
              </Button>
              <Button
                variant="outline"
                onClick={runReprocess}
                disabled={autoReprocessing}
                data-testid="auto-reprocess-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${autoReprocessing ? "animate-spin" : ""}`} />
                {autoReprocessing ? "Reprocessing…" : "Reprocess Documents"}
              </Button>
              <Button
                variant="outline"
                onClick={openDocs}
                data-testid="auto-view-docs-btn"
              >
                <FileText className="h-4 w-4 mr-1" />
                View Detected Documents
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detected Documents modal (Phase 2) */}
      <Dialog open={docsOpen} onOpenChange={setDocsOpen}>
        <DialogContent className="max-w-4xl" data-testid="auto-docs-modal">
          <DialogHeader>
            <DialogTitle>Detected Documents</DialogTitle>
            <DialogDescription>
              Invoice &amp; E-Way Bill attachments discovered in linked Gmail threads. Prepared for the next
              automation phase (no upload/extraction yet).
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-md border">
            <Table>
              <TableHeader className="sticky top-0 bg-white">
                <TableRow>
                  <TableHead>Quotation</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Attachment</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docsLoading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
                ) : docs.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground" data-testid="auto-docs-empty">No documents detected yet.</TableCell></TableRow>
                ) : (
                  docs.map((d, idx) => (
                    <TableRow key={idx} data-testid={`auto-doc-row-${idx}`}>
                      <TableCell className="font-medium">{d.quotation_number || d.quotation_id}<div className="text-[11px] text-muted-foreground">{d.seller_name || ""}</div></TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${d.attachment_type === "invoice" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-cyan-50 text-cyan-700 border-cyan-200"}`}>
                          {d.attachment_type === "invoice" ? "Invoice" : "E-Way Bill"}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">{d.attachment_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{d.file_size ? `${Math.round(d.file_size / 1024)} KB` : "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{d.received_date ? new Date(d.received_date).toLocaleString() : "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Migration Details modal (debug/verification) */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl" data-testid="gmail-details-modal">
          <DialogHeader>
            <DialogTitle>Migration Details</DialogTitle>
            <DialogDescription>
              Per-quotation results from the last historical email-linking run. For debugging and verification only.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-md border">
            <Table>
              <TableHeader className="sticky top-0 bg-white">
                <TableRow>
                  <TableHead>Quotation ID</TableHead>
                  <TableHead>Seller Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailsLoading ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
                ) : details.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground" data-testid="gmail-details-empty">No details available.</TableCell></TableRow>
                ) : (
                  details.map((r, idx) => (
                    <TableRow key={idx} data-testid={`gmail-detail-row-${idx}`}>
                      <TableCell className="font-medium">{r.quotation_id}</TableCell>
                      <TableCell>{r.seller_name}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                          r.status === "linked" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                          r.status === "not_found" ? "bg-amber-50 text-amber-700 border-amber-200" :
                          r.status === "skipped" ? "bg-slate-50 text-slate-600 border-slate-200" :
                          "bg-red-50 text-red-700 border-red-200"}`}>
                          {r.status === "linked" ? "Linked" :
                           r.status === "not_found" ? "Not Found" :
                           r.status === "skipped" ? "Skipped – Not Sent via Omnix" : "Error"}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.reason || "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Permissions Card */}
      <Card>
        <CardHeader>
          <CardTitle>Role Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-5 w-5 text-amber-600" />
                <h3 className="font-semibold text-amber-800">Admin</h3>
              </div>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Full access to Sellers, Products, Quotations</li>
                <li>• Full access to Discount Slabs (add/edit/delete)</li>
                <li>• Manage user roles via Control Panel</li>
                <li>• Access to Control Panel</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-cyan-50 border border-cyan-200">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-5 w-5 text-accent" />
                <h3 className="font-semibold text-primary">User</h3>
              </div>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Full access to Sellers, Products, Quotations</li>
                <li>• View only access to Discount Slabs</li>
                <li>• Cannot modify discount slabs</li>
                <li>• Cannot access Control Panel</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
